/**
 * useMyProfile — 내 계정 표시 정보(아바타 사진·휴대폰·법인별 부서/직위).
 * 설계: docs/design/12_통합관리_계정기기설계.md §8-A 머리(아바타·이름·소속·직위) · §8-D D-1.
 *
 * 소속 문서(tenants/{t}/members/{uid})는 운영에 아직 0건(§5-6 확인) — 없으면 법인 이름만 보여 준다.
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "./useAuth";
import { watchMyAccountDoc, watchMyProfilePhoto } from "@/services/my_account.repository";
import { membersRepo } from "@/services/members.repository";
import { SPACE_META, TENANT_KEYS, type TenantKey } from "@/domain/space";
import type { MyAccountDoc } from "@/domain/user";
import { isMemberActive, type MemberRow } from "@/domain/membership";

export interface MyProfileState {
  photo: string | null;
  account: MyAccountDoc | null;
  memberships: MemberRow[];
  /** 머리 한 줄 — "중앙산업 · 영업팀 · 과장" / "전체 관리자 · 중앙·경원" */
  affiliation: string;
}

export function useMyProfile(opts: { withAccount?: boolean } = {}): MyProfileState {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const withAccount = !!opts.withAccount;
  const [photo, setPhoto] = useState<string | null>(null);
  const [account, setAccount] = useState<MyAccountDoc | null>(null);
  const [mems, setMems] = useState<Partial<Record<TenantKey, MemberRow | null>>>({});

  useEffect(() => {
    if (!uid) return;
    return watchMyProfilePhoto(uid, setPhoto);
  }, [uid]);

  useEffect(() => {
    if (!uid || !withAccount) return;
    return watchMyAccountDoc(uid, setAccount, () => setAccount(null));
  }, [uid, withAccount]);

  // 소속 문서 — users.tenants 가 있으면 그 법인만, 없으면(소속 미설정) 읽지 않음
  const tenantsKey = (user?.tenants ?? []).join(",");
  useEffect(() => {
    if (!uid || !tenantsKey) return;
    const ts = tenantsKey.split(",").filter((t): t is TenantKey => (TENANT_KEYS as readonly string[]).includes(t));
    const unsubs = ts.map((t) =>
      membersRepo.watchOne(
        t,
        uid,
        (m) => setMems((prev) => ({ ...prev, [t]: m })),
        () => setMems((prev) => ({ ...prev, [t]: null })), // 읽을 수 없으면 없음으로
      ),
    );
    return () => unsubs.forEach((u) => u());
  }, [uid, tenantsKey]);

  const memberships = useMemo(
    // 지금 유효한 소속만(rules activeMember 와 같은 기준 — 종료일이 지난 소속 제외)
    () => (Object.values(mems).filter(Boolean) as MemberRow[]).filter((m) => isMemberActive(m)),
    [mems],
  );

  const affiliation = useMemo(() => {
    if (user?.isAdmin) {
      const ts = user.tenants?.length ? user.tenants : [...TENANT_KEYS];
      return `전체 관리자 · ${ts.map((t) => SPACE_META[t].short).join("·")}`;
    }
    const m = memberships[0];
    // 부서(role) 이름은 roles 구독이 필요해 머리 줄에는 넣지 않음 — 법인 · 통합 직급(소속 사본 rank)
    if (m) return [SPACE_META[m.tenant].label, m.rank].filter(Boolean).join(" · ");
    return (user?.tenants ?? []).map((t) => SPACE_META[t].label).join(" · ");
  }, [user, memberships]);

  return { photo: uid ? photo : null, account: uid && withAccount ? account : null, memberships, affiliation };
}
